import { PropsWithChildren, useContext, useEffect, useState } from "react";
import MainFrame from "../MainFrame";
import { useLocation, useNavigate, useParams } from "react-router";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Title from "../common/Title";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import React from "react";
import FormControl from "@mui/material/FormControl";
import HeadingOne from "../common/HeadingOne";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import {
  ActivityLog,
  ActivityLogType,
  DepartmentType,
  Staff,
  StaffDailySummary,
  StoreLocation,
} from "../common/models";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";
import { getLocalDate, getLocalTime } from "../common/helper";
import { addOrUpdateActivityLog, deleteStaff, getActivityLogsByStaff, updateStaff } from "../common/api";
import { Alert, Button, Dialog, DialogActions, DialogTitle, Divider, MenuItem, Modal, Select, Snackbar } from "@mui/material";
import WeekPicker from "../common/WeekPicker";
import { View } from "@aws-amplify/ui-react";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IUserContext, UserContext } from "../common/context";
var minMax = require("dayjs/plugin/minMax");
dayjs.extend(minMax);

interface Column {
  id: "token" | "amount" | "generatedAt";

  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};


interface WeekToDateTableColumn {
  id:
    | "date"
    | "checkedInAt"
    | "checkedOutAt"
    | "numberOfSales"
    | "totalSales"
    | "totalCommision"
    | "totalTip";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

interface WeekToDateTableProps {
  staff: Staff | undefined;
  activityLogsForWeek: ActivityLog[];
  storeLocation: StoreLocation;
  weekDates: dayjs.Dayjs[];
  hourlyRate: number;
  editOpen: boolean;
  setEditOpen: (b: boolean) => void;
}

const weekToDateTableColumns: readonly WeekToDateTableColumn[] = [
  { id: "date", label: "Date", minWidth: 100 },
  { id: "checkedInAt", label: "Checked In At", minWidth: 100 },
  { id: "checkedOutAt", label: "Checked Out At", minWidth: 100 },
  { id: "numberOfSales", label: "Num Sales", minWidth: 100 },
  {
    id: "totalSales",
    label: "Total Sales ($)",
    minWidth: 100,
    format: (value: number) => {
      return `\$${value}`;
    },
  },
  {
    id: "totalCommision",
    label: "Total Comm ($)",
    minWidth: 100,
    format: (value: number) => {
      return `\$${value}`;
    },
  },
  {
    id: "totalTip",
    label: "Total Tip ($)",
    minWidth: 100,
    format: (value: number) => {
      return `\$${value}`;
    },
  },
];

function getWeekDates(dateInput: dayjs.Dayjs | null): dayjs.Dayjs[] {
  if (dateInput == null) {
    return [];
  }
  const weekStart = dateInput.startOf("week"); // This gets Sunday as the start of the week by default

  const dates = [];
  for (let i = 0; i < 7; i++) {
    dates.push(weekStart.add(i, "day").startOf("day"));
  }
  return dates;
}

function WeekToDateTable(props: WeekToDateTableProps) {
  const activityLogsForWeek = props.activityLogsForWeek;
  const weekDates = props.weekDates.map((weekDate) =>
    weekDate.format("YYYY-MM-DD")
  );

  const [selectedRowDate, setSelectedRowDate] = useState<string | null>(null);
  const [newCheckedInTime, setNewCheckedInTime] = useState<dayjs.Dayjs | null>(null);
  const [newCheckedOutTime, setNewCheckedOutTime] = useState<dayjs.Dayjs | null>(null);

  const staffWeeklySummary = activityLogsForWeek.reduce(
    (
      activitySummaryPerDate: { [x: string]: StaffDailySummary },
      activityLog: ActivityLog
    ): { [x: string]: StaffDailySummary } => {
      const date = getLocalDate(dayjs(activityLog.createdAt))
        .format()
        .split("T")[0];
      if (!activitySummaryPerDate[date]) {
        activitySummaryPerDate[date] = {
          date: date,
          checkedInAt: null,
          checkedOutAt: null,
          numberOfSales: 0,
          totalSalesCard: 0,
          totalSalesCash: 0,
          totalGiftCard: 0,
          totalDeposit: 0,
          totalCommisionCard: 0,
          totalCommisionCash: 0,
          totalTipCard: 0,
          totalTipCash: 0,
        };
      }
      if (activityLog.type === ActivityLogType.CHECK_IN) {
        activitySummaryPerDate[date] = {
          date: date,
          checkedInAt: activityLog.createdAt,
          checkedOutAt: activitySummaryPerDate[date].checkedOutAt,
          numberOfSales: activitySummaryPerDate[date].numberOfSales,
          totalSalesCard: activitySummaryPerDate[date].totalSalesCard,
          totalSalesCash: activitySummaryPerDate[date].totalSalesCash,
          totalGiftCard: activitySummaryPerDate[date].totalGiftCard,
          totalDeposit: activitySummaryPerDate[date].totalDeposit,
          totalCommisionCard: activitySummaryPerDate[date].totalCommisionCard,
          totalCommisionCash: activitySummaryPerDate[date].totalCommisionCash,
          totalTipCard: activitySummaryPerDate[date].totalTipCard,
          totalTipCash: activitySummaryPerDate[date].totalTipCash,
        };
      } else if (activityLog.type === ActivityLogType.CHECK_OUT) {
        activitySummaryPerDate[date] = {
          date: date,
          checkedInAt: activitySummaryPerDate[date].checkedInAt,
          checkedOutAt: activityLog.createdAt,
          numberOfSales: activitySummaryPerDate[date].numberOfSales,
          totalSalesCard: activitySummaryPerDate[date].totalSalesCard,
          totalSalesCash: activitySummaryPerDate[date].totalSalesCash,
          totalCommisionCard: activitySummaryPerDate[date].totalCommisionCard,
          totalCommisionCash: activitySummaryPerDate[date].totalCommisionCash,
          totalGiftCard: activitySummaryPerDate[date].totalGiftCard,
          totalDeposit: activitySummaryPerDate[date].totalDeposit,
          totalTipCard: activitySummaryPerDate[date].totalTipCard,
          totalTipCash: activitySummaryPerDate[date].totalTipCash,
        };
      } else {
        const checkedInAt =
          activitySummaryPerDate[date].checkedInAt && activityLog.createdAt
            ? dayjs
                .min(
                  dayjs(activitySummaryPerDate[date].checkedInAt),
                  dayjs(activityLog.createdAt)
                )
                ?.toISOString()
            : activityLog.createdAt
            ? activityLog.createdAt
            : null;

        const checkedOutAt =
          activitySummaryPerDate[date].checkedOutAt && activityLog.createdAt
            ? dayjs
                .max(
                  dayjs(activitySummaryPerDate[date].checkedOutAt),
                  dayjs(activityLog.createdAt)
                )
                ?.toISOString()
            : activityLog.createdAt
            ? activityLog.createdAt
            : null;
        activitySummaryPerDate[date] = {
          date: date,
          checkedInAt: checkedInAt || null,
          checkedOutAt: checkedOutAt || null,
          numberOfSales: activitySummaryPerDate[date].numberOfSales + 1,
          totalSalesCard:
            activitySummaryPerDate[date].totalSalesCard +
            activityLog.saleAmountCard +
            (activityLog.giftCardAmount || 0) +
            (activityLog.depositAmount || 0),
          totalSalesCash:
            activitySummaryPerDate[date].totalSalesCash +
            activityLog.saleAmountCash,
          totalCommisionCard:
            activitySummaryPerDate[date].totalCommisionCard +
            activityLog.commissionAmountCard,
          totalGiftCard: activitySummaryPerDate[date].totalGiftCard + (activityLog.giftCardAmount || 0),
          totalDeposit: activitySummaryPerDate[date].totalDeposit + (activityLog.depositAmount || 0),
          totalCommisionCash:
            activitySummaryPerDate[date].totalCommisionCash +
            activityLog.commissionAmountCash,
          totalTipCard:
            activitySummaryPerDate[date].totalTipCard + activityLog.tipAmountCard,
          totalTipCash:
            activitySummaryPerDate[date].totalTipCash + activityLog.tipAmountCash,
        };
      }

      return activitySummaryPerDate;
    },
    {}
  );

  useEffect(() => {
    if (!selectedRowDate) {
      setNewCheckedInTime(null);
      setNewCheckedOutTime(null);
      return
    }

    setNewCheckedInTime(dayjs(staffWeeklySummary[selectedRowDate]?.checkedInAt) || null);
    setNewCheckedOutTime(dayjs(staffWeeklySummary[selectedRowDate]?.checkedOutAt) || null);

  }, [selectedRowDate])

  return (
    <React.Fragment>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Grid container justifyContent="flex-end">
          <CSVLink
            filename={`${props.staff?.name.toLowerCase().replace(/\s+/g, '-')}-${props.storeLocation.name.toLowerCase()}-${weekDates[0]}.csv`}
            data={[
              [
                "Date",
                "Checked In At",
                "Checked Out At",
                "Hourly Rate",
                "Hours Worked (-30 minute lunch)",
                "Number of Sales",
                "Total Sales",
                "Total Base",
                "Total Commission",
                "Total Tip",
              ],
              ...weekDates.map((weekDate) => {
                const summary = staffWeeklySummary[weekDate];
                
                if (summary) {
                  const lunchTime = 0.5;
                  const hoursWorked = Math.max(0, Math.round(100 * dayjs(summary.checkedOutAt).diff(
                    dayjs(summary.checkedInAt),
                    "hours",
                    true
                  )) / 100 - lunchTime)
                  return [
                    weekDate,
                    summary.checkedInAt
                      ? getLocalTime(summary.checkedInAt)
                      : null,
                    summary.checkedOutAt
                      ? getLocalTime(summary.checkedOutAt)
                      : null,
                    `\$${props.hourlyRate}`,
                    hoursWorked,
                    summary.numberOfSales || 0,
                    summary.totalSalesCard + summary.totalSalesCash || 0,
                    hoursWorked * props.hourlyRate,
                    summary.totalCommisionCard + summary.totalCommisionCash ||
                      0,
                    summary.totalTipCard + summary.totalTipCash || 0,
                  ];
                }
                return [weekDate, null, null, 0, 0, 0, 0, 0];
              }),
            ]}
          >
            Download
          </CSVLink>
        </Grid>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {weekToDateTableColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Modal open={selectedRowDate != null} onClose={() => setSelectedRowDate(null)}>
                    <Box  sx={style}>
                      <Title>{selectedRowDate}</Title>
                    <TimePicker
                      value={newCheckedInTime}
                      onChange={(val) => {
                        console.log(val);
                        setNewCheckedInTime(dayjs(selectedRowDate)
                          .hour(dayjs(val).hour())
                          .minute(dayjs(val).minute())
                          .second(dayjs(val).second()));
                      }}
                      label="Check In time"
                    />
                    <Divider style={{marginTop: 20, marginBottom: 20}}></Divider>
                    <TimePicker
                      value={newCheckedOutTime}
                      onChange={(val) => {
                        setNewCheckedOutTime(dayjs(selectedRowDate)
                          .hour(dayjs(val).hour())
                          .minute(dayjs(val).minute())
                          .second(dayjs(val).second()));
                      }}
                      label="Check out time"
                    />
                    <Divider style={{marginTop: 20, marginBottom: 20}}></Divider>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button onClick={() => {
                        if (!props.staff) {
                          return
                        }
                        const promises = [];

                        if (newCheckedInTime != null) {
                          console.log(props.activityLogsForWeek.find((activityLog: ActivityLog) => activityLog.type == ActivityLogType.CHECK_IN)?.id);
                          const checkInPromise = addOrUpdateActivityLog({
                            id: props.activityLogsForWeek.find((activityLog: ActivityLog) => getLocalDate(dayjs(activityLog.createdAt))
                            .format()
                            .split("T")[0] == selectedRowDate && activityLog.type == ActivityLogType.CHECK_IN)?.id,
                            createdAt: newCheckedInTime?.toISOString(),
                            staffID: props.staff.id,
                            storeLocationID: props.storeLocation.id,
                            type: ActivityLogType.CHECK_IN,
                            saleAmountCard: 0,
                            saleAmountCash: 0,
                            commissionAmountCard: 0,
                            commissionAmountCash: 0,
                            tipAmountCard: 0,
                            tipAmountCash: 0,
                            giftCardAmount: 0,
                            depositAmount: 0,
                          });
                          promises.push(checkInPromise); // Add the promise to the list
                        }
                        
                        if (newCheckedOutTime != null) {
                          const checkOutPromise = addOrUpdateActivityLog({
                            id: props.activityLogsForWeek.find((activityLog: ActivityLog) => getLocalDate(dayjs(activityLog.createdAt))
                            .format()
                            .split("T")[0] == selectedRowDate && activityLog.type == ActivityLogType.CHECK_OUT)?.id,
                            createdAt: newCheckedOutTime?.toISOString(),
                            staffID: props.staff.id,
                            storeLocationID: props.storeLocation.id,
                            type: ActivityLogType.CHECK_OUT,
                            saleAmountCard: 0,
                            saleAmountCash: 0,
                            commissionAmountCard: 0,
                            commissionAmountCash: 0,
                            tipAmountCard: 0,
                            tipAmountCash: 0,
                            giftCardAmount: 0,
                            depositAmount: 0,
                          });
                          promises.push(checkOutPromise); // Add the promise to the list
                        }
                        
                        // Run both promises in parallel
                        Promise.all(promises).then(() => {
                          setSelectedRowDate(null); // Only set after both operations complete
                          window.location.reload();
                        });
                      }}>Save</Button>
                    </Box>
                    </Box>
                  </Modal>
                </LocalizationProvider>
            {weekDates.map((date) => {
              const dailySummary = staffWeeklySummary[date];
              return (
                <>
                <TableRow onClick={() => setSelectedRowDate(date)}  hover role="checkbox" tabIndex={-1} key={date}>
                  {weekToDateTableColumns.map((column) => {
                    const value = dailySummary
                      ? {
                          date: date,
                          checkedInAt: dailySummary.checkedInAt
                            ? getLocalTime(dailySummary.checkedInAt)
                            : "--",
                          checkedOutAt: dailySummary.checkedOutAt
                            ? getLocalTime(dailySummary.checkedOutAt)
                            : "--",
                          numberOfSales: dailySummary.numberOfSales,
                          totalSales:
                            dailySummary.totalSalesCard +
                            dailySummary.totalSalesCash,
                          totalCommision:
                            dailySummary.totalCommisionCard +
                            dailySummary.totalCommisionCash,
                          totalTip:
                            dailySummary.totalTipCard +
                            dailySummary.totalTipCash,
                        }[column.id]
                      : {
                          date: date,
                          checkedInAt: "--",
                          checkedOutAt: "--",
                          numberOfSales: 0,
                          totalSales: 0,
                          totalCommision: 0,
                          totalTip: 0,
                        }[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
}

interface AlertMessage {
  message: string;
  severity: "success" | "info" | "warning" | "error" | undefined;
}

export default function StaffManagementPage(props: PropsWithChildren) {
  const location = useLocation();
  const [staff, setStaff] = React.useState<Staff>();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [activityLogsForWeek, setActivityLogsForWeek] = React.useState<
    {[k: string]: ActivityLog[]}
  >({});
  const [name, setName] = React.useState<string>();
  const [department, setDepartment] = React.useState<DepartmentType>();
  const [email, setEmail] = React.useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = React.useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = React.useState<number>();
  const [pin, setPin] = React.useState<string>();
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [weekDates, setWeekDates] = useState(getWeekDates(selectedDate));
  const [editPageOpen, setEditPageOpen] = useState(false);
  const navigate = useNavigate();

  const {storeLocations} = useContext<IUserContext>(UserContext);

  const storeLocationIDToObj = storeLocations.reduce((acc: {[key: string]: StoreLocation}, item: StoreLocation) => {
    acc[item.id] = item;
    return acc;
  }, {});

  const staffProps = location.state;

  const reset = (oldStaff: Staff) => {
    setName(oldStaff.name);
    setDepartment(oldStaff.department);
    setEmail(oldStaff.email);
    setPhoneNumber(oldStaff.phoneNumber);
    setHourlyRate(oldStaff.hourlyRate);
    setPin(oldStaff.pin);
    if (weekDates != null) {
      getActivityLogsByStaff(
        oldStaff.id,
        dayjs.min(weekDates) || dayjs().startOf("week"),
        dayjs.max(weekDates) || dayjs().endOf("week")
      ).then((activityLogs: ActivityLog[]) => {

      });
    }
  };

  useEffect(() => {
    setStaff(staffProps);
    reset(staffProps);
  }, []);

  useEffect(() => {
    const newWeekDates = getWeekDates(selectedDate);
    setWeekDates(newWeekDates);
    if (staff) {
      getActivityLogsByStaff(
        staff.id,
        dayjs.min(newWeekDates) || dayjs().startOf("week"),
        dayjs.max(newWeekDates)?.add(1, "day") || dayjs().endOf("week")
      ).then((activityLogs: ActivityLog[]) => {
        setActivityLogsForWeek(activityLogs.reduce((acc: {[k: string]: ActivityLog[]}, activityLog: ActivityLog) => {
          if (acc[activityLog.storeLocationID]) {
            acc[activityLog.storeLocationID].push(activityLog);
          } else {
            acc[activityLog.storeLocationID] = [activityLog];
          }
          return acc
        }, {}));
      });
    }
  }, [selectedDate]);


  return (
    <MainFrame>
      <Snackbar
        anchorOrigin={{
          horizontal: "center",
          vertical: "top",
        }}
        open={alertMessage != null}
        onClose={() => setAlertMessage(null)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setAlertMessage(null)}
          severity={alertMessage?.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage?.message}
        </Alert>
      </Snackbar>
      <Title>{staff?.name}</Title>
      <Container maxWidth="lg">
        <Grid container style={{ justifyContent: "flex-end" }}>
          {editMode ? (
            <>
                          <Button
                style={{ marginLeft: 5 }}
                variant="contained"
                color="error"
                onClick={() => {
                  setDeleteConfirmationOpen(true);
                }}
              >
                Delete
              </Button>
              <Dialog
        open={deleteConfirmationOpen}
        keepMounted
        onClose={()=> setDeleteConfirmationOpen(false)}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{`Are you sure you want to delete ${staff?.name}?`}</DialogTitle>
        <DialogActions>
          <Button onClick={() => {
            if (staff) {
              deleteStaff(staff).then((staff: Staff) => {
                setEditMode(false);
                setStaff(staff);
                setAlertMessage({
                  message: "Successfully deleted staff",
                  severity: "success",
                });

                navigate("/");
              });
            }
          }}>Yes</Button>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
              <Button
              style={{ marginLeft: 5 }}
                variant="contained"
                color="primary"
                onClick={() => {
                  if (name == null || name == "") {
                    setAlertMessage({
                      message: "Name cannot be empty",
                      severity: "error",
                    });
                    return;
                  }

                  if (department == null) {
                    setAlertMessage({
                      message: "Department cannot be empty",
                      severity: "error",
                    });
                    return;
                  }

                  if (hourlyRate && (hourlyRate <= 0 || hourlyRate > 100)) {
                    setAlertMessage({
                      message:
                        "Hourly rate cannot be less than or equal to 0 or greater than $100",
                      severity: "error",
                    });
                    return;
                  }

                  if (!pin) {
                    setAlertMessage({
                      message: "PIN must specified",
                      severity: "error",
                    });
                    return;
                  }

                  if (pin.length != 4) {
                    setAlertMessage({
                      message: "PIN must be of length 4",
                      severity: "error",
                    });
                    return;
                  }
                  if (
                    staff &&
                    staff.id &&
                    name &&
                    department &&
                    hourlyRate &&
                    pin &&
                    (name != staff.name ||
                      department != staff?.department ||
                      email != staff?.email ||
                      phoneNumber != staff?.phoneNumber ||
                      hourlyRate != staff?.hourlyRate ||
                      pin != staff?.pin)
                  ) {
                    updateStaff({
                      ...staff,
                      name: name.trim(),
                      department: department,
                      email: email ? email.trim() : null,
                      phoneNumber: phoneNumber,
                      hourlyRate: hourlyRate,
                      pin: pin,
                    }).then((staff: Staff) => {
                      setEditMode(false);
                      setStaff(staff);
                      setAlertMessage({
                        message: "Successfully updated staff",
                        severity: "success",
                      });
                    });
                  }
                }}
                disabled={
                  name?.trim() == staff?.name &&
                  department == staff?.department &&
                  email?.trim() == staff?.email &&
                  phoneNumber?.trim() == staff?.phoneNumber &&
                  hourlyRate == staff?.hourlyRate &&
                  pin == staff?.pin
                }
              >
                Save
              </Button>
              <Button
                style={{ marginLeft: 5 }}
                variant="outlined"
                color="error"
                onClick={() => {
                  setEditMode(false);
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setEditMode(true);
              }}
            >
              Edit
            </Button>
          )}
        </Grid>
        <Grid container paddingTop={2}>
          <TextField
            style={{ width: 250 }}
            label="Name"
            autoComplete="off"
            inputProps={{ autoComplete: "off" }}
            type="text"
            id="outlined-size-normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: name != staff?.name ? "green" : "grey",
                },
              },
            }}
            value={!editMode ? staff?.name : name}
            disabled={!editMode}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </Grid>
        <Grid container paddingTop={2}>
          <FormControl style={{ width: 200 }}>
            <InputLabel id="test-select-label">Department</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={!editMode ? staff?.department : department}
              key={staff?.department}
              disabled={!editMode}
              displayEmpty
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor:
                      department != staff?.department ? "green" : "grey",
                  },
                },
              }}
              onChange={(event) => {
                setDepartment(
                  DepartmentType[
                    event.target.value as keyof typeof DepartmentType
                  ]
                );
              }}
            >
              {Object.keys(DepartmentType).map((departmentType) => {
                return (
                  <MenuItem value={departmentType} key={departmentType}>
                    {departmentType}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            style={{ marginLeft: 10, width: 250 }}
            label="Email"
            autoComplete="off"
            inputProps={{ autoComplete: "off" }}
            type="email"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: email != staff?.email ? "green" : "grey",
                },
              },
            }}
            id="outlined-size-normal"
            value={!editMode ? staff?.email || "" : email}
            disabled={!editMode}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <TextField
            style={{ marginLeft: 10, width: 250 }}
            label="Phone Number"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: name != staff?.name ? "green" : "grey",
                },
              },
            }}
            id="outlined-size-normal"
            value={!editMode ? staff?.phoneNumber || "" : phoneNumber}
            disabled={!editMode}
            onChange={(event) => {
              setPhoneNumber(event.target.value);
            }}
          />
        </Grid>

        <Grid container paddingTop={2}>
          <FormControl sx={{ m: 1 }} variant="standard">
            <InputLabel>Hourly Rate</InputLabel>
            <Input
              id="outlined-size-normal"
              startAdornment={
                <InputAdornment position="start">$</InputAdornment>
              }
              type="number"
              value={!editMode ? staff?.hourlyRate : hourlyRate}
              disabled={!editMode}
              onChange={(event) => {
                setHourlyRate(parseFloat(event.target.value));
              }}
            ></Input>
          </FormControl>
          <FormControl sx={{ m: 1, width: "25ch" }} variant="standard">
            <InputLabel htmlFor="outlined-adornment-password">PIN</InputLabel>
            <Input
              id="outlined-adornment-password"
              value={!editMode ? staff?.pin : pin}
              inputProps={{ autoComplete: "off" }}
              disabled={!editMode}
              type="number"
              onChange={(event) => {
                setPin(event.target.value);
              }}
            />
          </FormControl>
        </Grid>

        <Box sx={{ marginTop: 5 }}>
          <HeadingOne>Weekly Summary</HeadingOne>
          <Grid container sm={3}>
            <WeekPicker setSelectedDate={setSelectedDate}></WeekPicker>
            <div></div>
          </Grid>
          {Object.keys(activityLogsForWeek).length > 0 ? Object.keys(activityLogsForWeek).map((storeLocationID: string) => {
            return <>
            <Title>{storeLocationIDToObj[storeLocationID]?.name}</Title>
            <WeekToDateTable
              activityLogsForWeek={activityLogsForWeek[storeLocationID]}
              storeLocation={storeLocationIDToObj[storeLocationID]}
              staff={staff}
              weekDates={weekDates}
              hourlyRate={hourlyRate || 0}
              editOpen={editPageOpen}
              setEditOpen={setEditPageOpen}
            /></>
          }):   <Title>{staff?.name} did not have any sales this week.</Title>}
          
        </Box>
      </Container>
    </MainFrame>
  );
}
